/* ============================================================
  FG Variant Availability (Radios) — position-aware (fixes “stuck”)
  - Correctly maps radio groups to Shopify option positions (1/2/3)
    even when option1 (e.g. Race) is NOT rendered as radios.
  - Disables + strike-through for unavailable values.
  - NEVER leaves the checked radio disabled.
============================================================ */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[id^="MainProduct-"], section[id*="__main"]';

  // ---------- CSS ----------
  function ensureCssOnce() {
    if (document.getElementById('fg-variant-availability-css')) return;
    var style = document.createElement('style');
    style.id = 'fg-variant-availability-css';
    style.textContent =
      '.fg-var-unavailable{opacity:.45;text-decoration:line-through;}' +
      'input.fg-var-unavailable{opacity:.55;}';
    document.head.appendChild(style);
  }

  // ---------- Helpers ----------
  function qsa(root, sel) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function normalise(s) {
    return String(s == null ? '' : s).trim();
  }

  function normaliseKey(s) {
    // Lowercase + strip accents + collapse spaces for robust matching
    return normalise(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getLabelForInput(input, scopeEl) {
    if (!input) return null;

    var id = input.getAttribute('id');
    if (id) {
      var byFor = (scopeEl || document).querySelector('label[for="' + CSS.escape(id) + '"]');
      if (byFor) return byFor;
    }

    var wrap = input.closest('label');
    if (wrap) return wrap;

    var parent = input.parentElement;
    if (parent) {
      var sibLabel = parent.querySelector('label');
      if (sibLabel) return sibLabel;
    }

    return null;
  }

  function setDisabledUI(inputEl, shouldDisable, scopeEl) {
    if (!inputEl) return;

    // Critical: never disable the currently selected radio
    var wantDisabled = !!shouldDisable;
    if (inputEl.checked) wantDisabled = false;

    inputEl.disabled = wantDisabled;
    if (wantDisabled) inputEl.setAttribute('disabled', '');
    else inputEl.removeAttribute('disabled');

    inputEl.setAttribute('aria-disabled', wantDisabled ? 'true' : 'false');

    inputEl.classList.toggle('disabled', wantDisabled);
    inputEl.classList.toggle('fg-var-unavailable', wantDisabled);

    var label = getLabelForInput(inputEl, scopeEl);
    if (label) {
      label.classList.toggle('disabled', wantDisabled);
      label.classList.toggle('fg-var-unavailable', wantDisabled);
    }
  }

  function forceCheckedRadiosEnabled(scopeEl) {
    if (!scopeEl) return;
    qsa(scopeEl, 'input[type="radio"][name]:checked').forEach(function (r) {
      r.disabled = false;
      r.removeAttribute('disabled');
      r.setAttribute('aria-disabled', 'false');
      r.classList.remove('disabled');
      r.classList.remove('fg-var-unavailable');

      var label = getLabelForInput(r, scopeEl);
      if (label) {
        label.classList.remove('disabled');
        label.classList.remove('fg-var-unavailable');
      }
    });
  }

  // ---------- Variants + option names ----------
  function getVariantsFromScope(scopeEl) {
    var candidates = [
      'script[type="application/json"][id^="ProductJson-"]',
      'script[type="application/json"][id*="ProductJson"]',
      'script[type="application/json"][data-product-json]',
      'script[type="application/json"][id*="product-json"]',
      'script[type="application/json"][id*="Product-"]'
    ];

    for (var i = 0; i < candidates.length; i++) {
      var el = scopeEl.querySelector(candidates[i]);
      if (!el) continue;
      try {
        var parsed = JSON.parse(el.textContent || '{}');
        if (Array.isArray(parsed)) return { variants: parsed, product: null };
        if (parsed && Array.isArray(parsed.variants)) return { variants: parsed.variants, product: parsed };
      } catch (e) {}
    }

    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product && Array.isArray(meta.product.variants)) {
        return { variants: meta.product.variants, product: meta.product };
      }
    } catch (e) {}

    return { variants: [], product: null };
  }

  function normaliseVariants(variantsRaw) {
    return (variantsRaw || []).map(function (v) {
      var available =
        (typeof v.available === 'boolean')
          ? v.available
          : (typeof v.availableForSale === 'boolean')
          ? v.availableForSale
          : !!v.available;

      return {
        id: v.id,
        available: available,
        option1: normalise(v.option1),
        option2: normalise(v.option2),
        option3: normalise(v.option3)
      };
    });
  }

  function getOptionNames(productObj) {
    // Try multiple common shapes
    // - ShopifyAnalytics.meta.product.options (array of strings)
    // - product JSON "options" (array of strings)
    // - product JSON "options_with_values" (array of {name})
    try {
      if (productObj && Array.isArray(productObj.options) && productObj.options.length) {
        return productObj.options.slice(0, 3);
      }
    } catch (e) {}

    try {
      if (productObj && Array.isArray(productObj.options_with_values) && productObj.options_with_values.length) {
        return productObj.options_with_values.map(function (o) { return o && o.name; }).slice(0, 3);
      }
    } catch (e) {}

    // Last chance: ShopifyAnalytics meta sometimes exposes option names as "option1", etc
    return [];
  }

  // ---------- Build groups + map to option positions ----------
  function buildGroups(scopeEl, optionNames) {
    var form = scopeEl.querySelector('form[action*="/cart/add"]') || scopeEl;
    var radios = qsa(form, 'input[type="radio"][name]');

    if (!radios.length) return [];

    var map = {};
    radios.forEach(function (r) {
      var name = r.getAttribute('name');
      if (!name) return;
      if (!map[name]) map[name] = { name: name, radios: [] };
      map[name].radios.push(r);
    });

    // Preserve DOM order by first occurrence
    var orderedNames = [];
    radios.forEach(function (r) {
      var n = r.getAttribute('name');
      if (n && orderedNames.indexOf(n) === -1) orderedNames.push(n);
    });

    // Map group -> option position (1/2/3) by matching names
    var optMap = {};
    (optionNames || []).forEach(function (n, idx) {
      if (!n) return;
      optMap[normaliseKey(n)] = idx + 1; // 1-based
    });

    return orderedNames.map(function (n, idx) {
      var g = map[n];
      var key = normaliseKey(n);
      var pos = optMap[key] || (idx + 1); // fallback to DOM order if not detectable
      g.pos = pos; // 1..3
      return g;
    });
  }

  function getSelectionsByPos(groups) {
    // pos 1..3
    var sel = { 1: '', 2: '', 3: '' };
    groups.forEach(function (g) {
      var checked = g.radios.find(function (r) { return r.checked; });
      sel[g.pos] = checked ? normalise(checked.value) : '';
    });
    return sel;
  }

  function variantValueAtPos(variant, pos) {
    if (pos === 1) return variant.option1;
    if (pos === 2) return variant.option2;
    return variant.option3;
  }

  function matchesOtherPositions(variant, selectionsByPos, skipPos) {
    for (var pos = 1; pos <= 3; pos++) {
      if (pos === skipPos) continue;
      var wanted = selectionsByPos[pos];
      if (!wanted) continue; // unconstrained
      if (normalise(variantValueAtPos(variant, pos)) !== normalise(wanted)) return false;
    }
    return true;
  }

  function isValueAvailableForGroup(variants, selectionsByPos, groupPos, candidateValue) {
    var want = normalise(candidateValue);

    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (!v.available) continue;

      if (normalise(variantValueAtPos(v, groupPos)) !== want) continue;
      if (matchesOtherPositions(v, selectionsByPos, groupPos)) return true;
    }
    return false;
  }

  function recomputeDisabledStates(scopeEl, variants, groups) {
    var selectionsByPos = getSelectionsByPos(groups);

    groups.forEach(function (g) {
      g.radios.forEach(function (r) {
        var ok = isValueAvailableForGroup(variants, selectionsByPos, g.pos, r.value);
        setDisabledUI(r, !ok, scopeEl);
      });
    });

    forceCheckedRadiosEnabled(scopeEl);
  }

  function autoFixInvalidSelection(scopeEl, variants, groups) {
    var changed = false;

    // Ensure each group has a checked value that isn’t disabled
    groups.forEach(function (g) {
      var checked = g.radios.find(function (r) { return r.checked; });

      if (!checked || checked.disabled) {
        var pick = g.radios.find(function (r) { return !r.disabled; });
        if (pick) {
          pick.checked = true;
          pick.dispatchEvent(new Event('input', { bubbles: true }));
          pick.dispatchEvent(new Event('change', { bubbles: true }));
          changed = true;
        }
      }
    });

    if (changed) forceCheckedRadiosEnabled(scopeEl);
  }

  function sync(scopeEl, variants, groups) {
    ensureCssOnce();

    // Always clear “checked but disabled” first
    forceCheckedRadiosEnabled(scopeEl);

    recomputeDisabledStates(scopeEl, variants, groups);
    autoFixInvalidSelection(scopeEl, variants, groups);
    recomputeDisabledStates(scopeEl, variants, groups);

    // Late passes to win any tug-of-war
    setTimeout(function () { forceCheckedRadiosEnabled(scopeEl); }, 0);
    setTimeout(function () { forceCheckedRadiosEnabled(scopeEl); }, 80);
    requestAnimationFrame(function () { forceCheckedRadiosEnabled(scopeEl); });
  }

  function initSection(scopeEl) {
    if (!scopeEl || scopeEl.__fgVarAvailInit) return;
    scopeEl.__fgVarAvailInit = true;

    var data = getVariantsFromScope(scopeEl);
    var variants = normaliseVariants(data.variants);
    if (!variants.length) return;

    var optionNames = getOptionNames(data.product);
    var groups = buildGroups(scopeEl, optionNames);
    if (!groups.length) return;

    sync(scopeEl, variants, groups);

    // Rebuild groups on every change (DOM may be re-rendered)
    scopeEl.addEventListener(
      'change',
      function (e) {
        var t = e && e.target;
        if (!t || !(t.matches && t.matches('input[type="radio"][name]'))) return;
        groups = buildGroups(scopeEl, optionNames);
        sync(scopeEl, variants, groups);
      },
      true
    );

    document.addEventListener('variant:change', function () {
      groups = buildGroups(scopeEl, optionNames);
      sync(scopeEl, variants, groups);
    });

    document.addEventListener('shopify:section:load', function (evt) {
      if (!evt || !evt.target) return;
      if (scopeEl.contains(evt.target) || evt.target.contains(scopeEl)) {
        groups = buildGroups(scopeEl, optionNames);
        sync(scopeEl, variants, groups);
      }
    });

    // If something sets disabled after the click, undo if it’s checked
    try {
      var obs = new MutationObserver(function () {
        forceCheckedRadiosEnabled(scopeEl);
      });
      obs.observe(scopeEl, {
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class']
      });
    } catch (e) {}
  }

  function boot() {
    qsa(document, ROOT_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();